import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import './Register.css';

function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fname: '',
    lname: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [quizPassed, setQuizPassed] = useState(false);
  
  // Check if user passed quiz before registration
  useEffect(() => {
    const passed = localStorage.getItem('quiz_passed') === 'true';
    setQuizPassed(passed);
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Client-side validation
    if (formData.password !== formData.password_confirmation) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await authApi.register({
        fname: formData.fname,
        lname: formData.lname,
        email: formData.email,
        phone: formData.phone || null,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        quiz_passed: quizPassed, // Send quiz pass flag to backend
      });
      
      // Clear quiz pass flag after successful registration
      if (quizPassed) {
        localStorage.removeItem('quiz_passed');
      }
      
      // Handle API response structure: { success: true, message: "...", data: { user: {...} } }
      if (response.data.success) {
        // Registration successful - show code input form
        setSuccess(response.data.message || 'Registration successful! Please check your email for the verification code.');
        setUserEmail(response.data.data?.user?.email || formData.email);
        setShowCodeInput(true);
        
        // Clear form
        setFormData({
          fname: '',
          lname: '',
          email: '',
          phone: '',
          password: '',
          password_confirmation: '',
        });

        // In debug mode, show verification code if provided
        if (response.data.verification_code) {
          setSuccess(response.data.message + ' Verification Code: ' + response.data.verification_code);
        }
      } else {
        setError(response.data.message || 'Registration failed');
      }
    } catch (err) {
      // Handle validation errors
      if (err.response?.data?.errors) {
        const errorMessages = Object.values(err.response.data.errors).flat();
        setError(errorMessages.join(', ') || 'Registration failed');
      } else {
        setError(
          err.response?.data?.message || 
          'Registration failed. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError('');

    try {
      const response = await authApi.verifyEmail(userEmail, verificationCode);
      
      if (response.data.success) {
        setSuccess('Email verified successfully! You can now log in.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(response.data.message || 'Invalid verification code.');
      }
    } catch (err) {
      setError(
        err.response?.data?.message || 
        'Failed to verify code. Please check the code and try again.'
      );
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      <SEO
        title="Register"
        description="Create a GuyRules account to submit rules, vote, and join Club Guydom."
        canonicalPath="/register"
      />
      <Header />
      <div className="container">
        <div className="register-page">
          <h1>Register</h1>
          
          {quizPassed && (
            <div className="alert alert-success" style={{ 
              padding: '1rem', 
              backgroundColor: '#d4edda', 
              border: '1px solid #c3e6cb', 
              borderRadius: '4px',
              color: '#155724',
              marginBottom: '1rem'
            }}>
              <strong>🎉 Congratulations on passing the quiz!</strong>
              <p style={{ margin: '0.5rem 0 0 0' }}>
                After successful registration and email verification, your account will automatically be set to <strong>Club member</strong> with access to:
              </p>
              <ul style={{ textAlign: 'left', marginTop: '0.5rem', marginBottom: 0 }}>
                <li>Submit new rules for voting</li>
                <li>Vote on club submissions</li>
                <li>Access club-only features</li>
              </ul>
            </div>
          )}
          
          {error && <div className="error-message">{error}</div>}
          {success && !showCodeInput && (
            <div className="alert alert-success" style={{ 
              padding: '1rem', 
              backgroundColor: '#d4edda', 
              border: '1px solid #c3e6cb', 
              borderRadius: '4px',
              color: '#155724',
              marginBottom: '1rem'
            }}>
              {success}
            </div>
          )}

          {showCodeInput && (
            <div style={{ marginBottom: '1rem' }}>
              <div className="alert alert-info" style={{ 
                padding: '1rem', 
                backgroundColor: '#d1ecf1', 
                border: '1px solid #bee5eb', 
                borderRadius: '4px',
                color: '#0c5460',
                marginBottom: '1rem'
              }}>
                <p style={{ margin: 0 }}>{success}</p>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                  Enter the 6-digit code sent to <strong>{userEmail}</strong>
                </p>
              </div>

              <form onSubmit={handleVerifyCode}>
                <div className="form-group">
                  <label htmlFor="verificationCode">Verification Code</label>
                  <input
                    type="text"
                    id="verificationCode"
                    name="verificationCode"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setVerificationCode(value);
                      setError('');
                    }}
                    required
                    maxLength={6}
                    disabled={verifying}
                    placeholder="000000"
                    style={{
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      letterSpacing: '0.5rem',
                      fontFamily: 'monospace',
                      fontWeight: 'bold'
                    }}
                    autoComplete="off"
                  />
                  <small>Enter the 6-digit code from your email</small>
                </div>

                {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}

                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={verifying || verificationCode.length !== 6}
                  style={{ width: '100%', marginBottom: '0.5rem' }}
                >
                  {verifying ? 'Verifying...' : 'Verify Email'}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate('/resend-verification')}
                  style={{ width: '100%' }}
                >
                  Resend Code
                </button>
              </form>
            </div>
          )}
          
          {!success && !showCodeInput && (
            <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="fname">First Name</label>
              <input
                type="text"
                id="fname"
                name="fname"
                value={formData.fname}
                onChange={handleChange}
                required
                autoComplete="given-name"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="lname">Last Name</label>
              <input
                type="text"
                id="lname"
                name="lname"
                value={formData.lname}
                onChange={handleChange}
                required
                autoComplete="family-name"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone (Optional)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                autoComplete="tel"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                disabled={loading}
                minLength={8}
              />
              <small>Must be at least 8 characters</small>
            </div>

            <div className="form-group">
              <label htmlFor="password_confirmation">Confirm Password</label>
              <input
                type="password"
                id="password_confirmation"
                name="password_confirmation"
                value={formData.password_confirmation}
                onChange={handleChange}
                required
                autoComplete="new-password"
                disabled={loading}
                minLength={8}
              />
            </div>

            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Registering...' : 'Register'}
            </button>
          </form>
          )}

          <p className="login-link" style={{ marginTop: '1rem' }}>
            Already have an account? <Link to="/login">Login here</Link>
          </p>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Register;
